import React from "react";
import { Box, Portal, Typography } from "@mui/material";

export interface TimelineDatePreviewTooltipItem {
  label: string;
  value: string;
}

interface TimelineDatePreviewTooltipProps {
  clientX: number;
  clientY: number;
  items: TimelineDatePreviewTooltipItem[];
  testId?: string;
}

const TOOLTIP_VIEWPORT_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 520;

const PreviewValue: React.FC<TimelineDatePreviewTooltipItem> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      minWidth: 0,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: "rgba(255, 255, 255, 0.64)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: "12px",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        color: "#ffffff",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: "14px",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const TimelineDatePreviewTooltip = ({
  clientX,
  clientY,
  items,
  testId = "timeline-drag-date-preview",
}: TimelineDatePreviewTooltipProps) => (
  <Portal>
    <Box
      role="status"
      aria-live="polite"
      data-testid={testId}
      sx={{
        position: "fixed",
        top: `clamp(${TOOLTIP_VIEWPORT_MARGIN}px, ${
          clientY + 18
        }px, calc(100vh - 76px))`,
        left: `clamp(${TOOLTIP_VIEWPORT_MARGIN}px, ${
          clientX + 14
        }px, calc(100vw - ${TOOLTIP_MAX_WIDTH + TOOLTIP_VIEWPORT_MARGIN}px))`,
        zIndex: 30060,
        display: "grid",
        gridTemplateColumns: `repeat(${items.length}, max-content)`,
        alignItems: "center",
        gap: 1.25,
        width: "max-content",
        minWidth: `min(284px, calc(100vw - ${TOOLTIP_VIEWPORT_MARGIN * 2}px))`,
        maxWidth: `min(${TOOLTIP_MAX_WIDTH}px, calc(100vw - ${
          TOOLTIP_VIEWPORT_MARGIN * 2
        }px))`,
        boxSizing: "border-box",
        px: 1.25,
        py: 0.75,
        borderRadius: "6px",
        backgroundColor: "rgba(17, 24, 39, 0.96)",
        border: "1px solid rgba(255, 255, 255, 0.14)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.26)",
        pointerEvents: "none",
      }}
    >
      {items.map((item) => (
        <PreviewValue key={item.label} {...item} />
      ))}
    </Box>
  </Portal>
);
